import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, CheckCircle2, ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { waLink, IMG_ALTS, srcSetFor } from "@/lib/content";
import { PROFILES } from "@/lib/profilesData";
import { trackContactForm } from "@/lib/analytics";
import { LeadForm } from "./LeadSection";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;

export const ProfilePage = ({ slug }) => {
  const d = PROFILES[slug];
  const [form, setForm] = useState({ name: "", phone: "", message: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!d) return;
    document.title = d.title;
    return () => {
      document.title = "Melo Agro Engenharia | Crédito rural e laudos em Pelotas/RS";
    };
  }, [d]);

  if (!d) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!API) return toast.error("Envio indisponível. Fale conosco pelo WhatsApp.");
    if (!form.consent) return toast.error("É preciso aceitar a Política de Privacidade.");
    trackContactForm(window.location.pathname);
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, email: "", service: d.eyebrow, profile: d.profile, website: e.currentTarget.website?.value || "" });
      setSent(true);
      setForm({ name: "", phone: "", message: "", consent: false });
      toast.success("Mensagem enviada! Retornaremos em breve.");
    } catch (err) {
      toast.error("Não foi possível enviar. Fale pelo WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "mt-2 w-full border-b border-[#1A2218]/20 bg-transparent py-2 text-[#1A2218] outline-none transition-colors duration-300 focus:border-[#5D995E]";

  return (
    <main data-testid={`profile-page-${slug}`} className="pt-[72px]">
      <section className="grain relative overflow-hidden bg-[#1B2F1D] text-[#F9F8F3]">
        <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-10 px-5 pb-16 pt-16 sm:px-8 md:pt-20 lg:grid-cols-12">
          <div className="relative z-10 lg:col-span-7">
            <Reveal><Overline light>{d.eyebrow}</Overline></Reveal>
            <h1 data-testid={`profile-h1-${slug}`} className="mt-6 font-serif text-4xl font-semibold leading-[1.05] tracking-[-0.03em] sm:text-5xl lg:text-6xl">
              {d.h1}
            </h1>
            <Reveal delay={0.1}>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#F9F8F3]/70 md:text-lg">{d.sub}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-[#F9F8F3]/80 md:text-lg">{d.intro}</p>
              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <a
                  data-testid={`profile-wa-cta-${slug}`}
                  href={waLink(d.waMsg)}
                  target="_blank"
                  rel="noreferrer"
                  className="group inline-flex items-center justify-center gap-3 rounded-full bg-[#F9F8F3] px-7 py-4 text-sm font-semibold text-[#16220F] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#8FC690]"
                >
                  {d.waCta}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
                </a>
                <a
                  data-testid={`profile-form-link-${slug}`}
                  href="#formulario-perfil"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[#F9F8F3]/30 px-7 py-4 text-sm font-semibold transition-colors duration-300 hover:border-[#8FC690]"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("formulario-perfil")?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  Enviar mensagem pelo site
                </a>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1} className="overflow-hidden rounded-[24px] rounded-tr-[90px]">
              <img
              src={d.image}
              alt={IMG_ALTS[d.image] || d.sub}
              srcSet={srcSetFor(d.image)}
              sizes="(min-width:1024px) 40vw, calc(100vw - 40px)"
              width={1600}
              height={1200}
              loading="lazy"
              className="aspect-[4/3] w-full object-cover"
            />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8 md:py-28">
        <Reveal><Overline>O que fazemos para você</Overline></Reveal>
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {d.offerings.map((o, i) => {
            const body = (
              <div className="flex h-full flex-col rounded-[24px] border border-[#E5E0D5] bg-white p-7 transition-[box-shadow,transform] duration-500 hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(31,58,36,0.4)]">
                <span className="font-mono text-xs tracking-[0.15em] text-[#5D995E]">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="mt-4 font-serif text-2xl leading-snug text-[#1A2218]">{o.t}</h2>
                <p className="mt-3 flex-1 text-base leading-relaxed text-[#4A5549]">{o.d}</p>
                {o.link && (
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#233D25]">
                    Ver serviço <ArrowUpRight className="h-4 w-4" />
                  </span>
                )}
              </div>
            );
            return (
              <Reveal key={o.t} delay={0.05 * i}>
                {o.link ? (
                  <Link to={o.link} data-testid={`profile-offering-${slug}-${i + 1}`} className="block h-full">
                    {body}
                  </Link>
                ) : (
                  <div data-testid={`profile-offering-${slug}-${i + 1}`} className="h-full">
                    {body}
                  </div>
                )}
              </Reveal>
            );
          })}
        </div>

        <div className="mt-20 grid grid-cols-1 gap-14 lg:grid-cols-12 lg:items-start">
          <div className="lg:col-span-6">
            <Reveal><Overline>Quando nos procurar</Overline></Reveal>
            <ul className="mt-8 space-y-3">
              {d.problems.map((p) => (
                <li key={p} data-testid={`profile-problem-${slug}`} className="flex items-start gap-3">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-[#5D995E]" />
                  <span className="text-base leading-relaxed text-[#2C332A]/90">{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <Reveal><Overline>{d.howTitle}</Overline></Reveal>
            <div className="mt-8 space-y-6">
              {d.steps.map((st, i) => (
                <div key={st.t} data-testid={`profile-step-${slug}-${i + 1}`} className="flex gap-5">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#233D25] font-mono text-xs text-[#8FC690]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <p className="font-serif text-xl text-[#1A2218]">{st.t}</p>
                    <p className="mt-1 text-base leading-relaxed text-[#4A5549]">{st.d}</p>
                  </div>
                </div>
              ))}
            </div>
            {d.delivery && (
              <Reveal>
                <div data-testid={`profile-delivery-${slug}`} className="mt-10 rounded-[24px] border border-[#E5E0D5] bg-[#F2EFE9] p-7">
                  <h2 className="font-serif text-2xl text-[#1A2218]">{d.deliveryTitle}</h2>
                  <dl className="mt-5 space-y-4">
                    {d.delivery.map((it) => (
                      <div key={it.t} className="border-t border-[#E5E0D5] pt-3">
                        <dt className="font-mono text-xs uppercase tracking-[0.15em] text-[#4A5549]">{it.t}</dt>
                        <dd className="mt-1 text-base leading-relaxed text-[#2C332A]">{it.d}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      <section id="formulario-perfil" className="bg-[#F2EFE9] py-16 md:py-28">
        <div className="mx-auto max-w-[900px] px-5 sm:px-8">
          {d.leadMagnet && (
            <Reveal>
              <div data-testid={`lead-${slug}`} className="mb-8 rounded-[28px] bg-[#1B2F1D] p-6 text-[#F9F8F3] sm:p-10">
                <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#8FC690]">Material grátis</p>
                <h2 className="mt-3 font-serif text-3xl leading-snug sm:text-4xl">{d.leadMagnet.title}</h2>
                <p className="mt-3 max-w-2xl text-base leading-relaxed text-[#F9F8F3]/75">{d.leadMagnet.desc}</p>
                <div className="mt-6 rounded-[20px] bg-white p-5 text-[#1A2218] sm:p-7">
                  <LeadForm material={d.leadMagnet} testid={`lead-${slug}`} />
                </div>
              </div>
            </Reveal>
          )}
          <Reveal>
            <div data-testid={`profile-form-${slug}`} className="rounded-[28px] border border-[#E5E0D5] bg-white p-7 shadow-[0_30px_80px_-40px_rgba(31,58,36,0.35)] sm:p-10">
              <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#5D995E]">Você é: {d.profile}</p>
              <h2 className="mt-3 font-serif text-3xl text-[#1A2218] sm:text-4xl">Envie a sua mensagem</h2>
              <form onSubmit={submit} className="mt-8 space-y-6">
                <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

                <input
                  required
                  minLength={2}
                  data-testid={`profile-form-name-${slug}`}
                  placeholder="Seu nome"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className={inputCls}
                />
                <input
                  data-testid={`profile-form-phone-${slug}`}
                  placeholder="Telefone / WhatsApp"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  className={inputCls}
                />
                <textarea
                  required
                  minLength={5}
                  rows={4}
                  data-testid={`profile-form-message-${slug}`}
                  placeholder="Conte o que você precisa — tanto quanto quiser"
                  value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  className={`${inputCls} resize-none`}
                />
                <label className="flex items-start gap-3 text-sm text-[#4A5549]">
                  <input
                    type="checkbox"
                    required
                    checked={form.consent}
                    onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                    data-testid={`profile-form-consent-${slug}`}
                    className="mt-0.5 h-4 w-4 accent-[#233D25]"
                  />
                  <span>
                    Autorizo o contato da Melo Agro Engenharia e li a{" "}
                    <Link to="/politica-de-privacidade" className="font-semibold text-[#233D25] underline underline-offset-2">Política de Privacidade</Link>. *
                  </span>
                </label>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid={`profile-form-submit-${slug}`}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#233D25] px-6 py-4 font-semibold text-[#F9F8F3] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#3E5B3F] disabled:opacity-70 sm:w-auto"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : sent ? <CheckCircle2 className="h-5 w-5" /> : null}
                  {loading ? "Enviando..." : "Enviar mensagem"}
                </button>
                {sent && <p data-testid={`profile-form-success-${slug}`} className="text-sm font-medium text-[#233D25]">Recebemos sua mensagem! Retornamos em até 1 dia útil pelo WhatsApp.</p>}
              </form>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};
